import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ref, onValue, set, update, get } from 'firebase/database';
import { database } from './firebase-config';
import { v4 as uuidv4 } from 'uuid';

// Define the activity data structure for each user
interface UserActivityData {
  totalCAP: number;
  remainingCAP: number;
  spentActivities: Record<string, number>;
  dailyActivities: Record<string, string[]>;
}

// Define the shape of our session data
interface SessionData {
  id: string;
  createdAt: number;
  lastUpdated: number;
  activeUsers: Record<string, { 
    name: string; 
    lastActive: number;
    role: 'DM' | 'Player';
  }>;
  gameState: {
    moraleState: 'Mutinous' | 'Resentful' | 'Unnerved' | 'Discouraged' | 'Normal' | 'Heartened';
    cleanlinessState: 'Hazardous' | 'Neglected' | 'Standard' | 'Pristine';
    navigationState: 'On course' | 'Off course' | 'Lost at sea';
    weatherData: {
      climate: string;
      temperature: string;
      wind: string;
      windStrength: string;
      windDirection: string;
      precipitation: string;
    };
    upperDeckSections: Array<{
      current: number;
      max: number;
      hasDealtHoleDamage?: boolean;
    }>;
    bottomDeckSections: Array<{
      current: number;
      max: number;
      hasDealtHoleDamage?: boolean;
    }>;
    riggingSections: Array<{
      current: number;
      max: number;
    }>;
    // Removed individual activity tracking from global state
    // spentActivities: Record<string, number>;
    // dailyActivities: Record<string, string[]>;
    // remainingCAP: number;
    // totalCAP: number;
    
    // Added shared days in turn and per-user activity data
    sharedDaysInTurn: number;
    userActivities: Record<string, UserActivityData>;
    
    bonuses: {
      professionSailor: number;
      balanceAndClimb: number;
      pilot: number;
      allSkillChecks: number;
      shipWeaponAttacks: number;
      nonShipWeaponAttacks: number;
      temporaryHP: number;
      conSavesAgainstPoisonAndDisease: number;
    };
    roleVacancies: {
      captain: boolean;
      firstMate: boolean;
      pilot: boolean;
    };
  };
}

// Default state for a new session
const defaultGameState = {
  moraleState: 'Normal' as const,
  cleanlinessState: 'Standard' as const,
  navigationState: 'On course' as const,
  weatherData: {
    climate: 'Temperate',
    temperature: '',
    wind: '',
    windStrength: '',
    windDirection: '',
    precipitation: '',
  },
  upperDeckSections: Array(12).fill({ current: 80, max: 80, hasDealtHoleDamage: false }),
  bottomDeckSections: Array(12).fill({ current: 80, max: 80, hasDealtHoleDamage: false }),
  riggingSections: Array(3).fill({ current: 80, max: 80 }),
  
  // Removed from global state, now per-user
  // spentActivities: {},
  // dailyActivities: { 'Day 1': [], 'Day 2': [], 'Day 3': [] },
  // remainingCAP: 20,
  // totalCAP: 20,
  
  // Added shared days in turn and per-user activity tracking
  sharedDaysInTurn: 3,
  userActivities: {},
  
  bonuses: {
    professionSailor: 0,
    balanceAndClimb: 0,
    pilot: 0,
    allSkillChecks: 0,
    shipWeaponAttacks: 0,
    nonShipWeaponAttacks: 0,
    temporaryHP: 0,
    conSavesAgainstPoisonAndDisease: 0,
  },
  roleVacancies: {
    captain: false,
    firstMate: false,
    pilot: false,
  },
};

// Default user activity data
const defaultUserActivityData: UserActivityData = {
  totalCAP: 1,
  remainingCAP: 1,
  spentActivities: {},
  dailyActivities: { 'Day 1': [], 'Day 2': [], 'Day 3': [] }
};

interface SessionContextType {
  sessionId: string | null;
  userId: string;
  userName: string;
  setUserName: (name: string) => void;
  gameState: SessionData['gameState'] | null;
  createSession: (asDM: boolean) => Promise<string>;
  joinSession: (id: string, asDM: boolean) => Promise<boolean>;
  leaveSession: () => void;
  updateGameState: <K extends keyof SessionData['gameState']>(
    key: K,
    value: SessionData['gameState'][K]
  ) => Promise<void>;
  updateUserActivity: (
    userId: string,
    activityData: UserActivityData
  ) => Promise<void>;
  updateSharedDaysInTurn: (days: number) => Promise<void>;
  activeUsers: Record<string, { 
    name: string; 
    lastActive: number; 
    role: 'DM' | 'Player' 
  }>;
  isDM: () => boolean;
  canClaimDM: () => boolean;
  claimDMRole: () => Promise<boolean>;
  releaseDMRole: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider = ({ children }: { children: React.ReactNode }) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [userId] = useState<string>(() => {
    const storedId = localStorage.getItem('userId');
    if (storedId) return storedId;
    const newId = uuidv4();
    localStorage.setItem('userId', newId);
    return newId;
  });
  const [userName, setUserName] = useState<string>(() => {
    return localStorage.getItem('userName') || 'Anonymous';
  });
  const [gameState, setGameState] = useState<SessionData['gameState'] | null>(null);
  const [activeUsers, setActiveUsers] = useState<Record<string, { name: string; lastActive: number; role: 'DM' | 'Player' }>>({});
  
  const navigate = useNavigate();
  const location = useLocation();

  // Update local storage when userName changes
  useEffect(() => {
    localStorage.setItem('userName', userName);
  }, [userName]);

  // Handle user presence and session cleanup
  useEffect(() => {
    if (!sessionId || !userId) return;

    // Reference to the current user's presence in this session
    const userPresenceRef = ref(database, `sessions/${sessionId}/activeUsers/${userId}`);
    const userStatusData = {
      name: userName,
      lastActive: Date.now(),
      role: activeUsers[userId]?.role || 'Player'
    };

    // Set the initial presence
    set(userPresenceRef, userStatusData);
    
    // Initialize user activity data if not already present
    const userActivityRef = ref(database, `sessions/${sessionId}/gameState/userActivities/${userId}`);
    get(userActivityRef).then((snapshot) => {
      if (!snapshot.exists()) {
        // Initialize the user's activity data with default values
        set(userActivityRef, defaultUserActivityData);
      }
    });

    // Set up a presence system with a heartbeat
    const heartbeatInterval = setInterval(() => {
      update(userPresenceRef, { lastActive: Date.now() });
    }, 30000); // Update every 30 seconds

    // Listen for active users changes
    const activeUsersRef = ref(database, `sessions/${sessionId}/activeUsers`);
    const unsubscribeActiveUsers = onValue(activeUsersRef, (snapshot) => {
      const data = snapshot.val() || {};
      setActiveUsers(data);
      
      // Clean up inactive users (>5 minutes without updates)
      const now = Date.now();
      const inactiveTimeout = 5 * 60 * 1000; // 5 minutes
      
      Object.entries(data).forEach(([uid, userData]) => {
        const user = userData as { lastActive: number };
        if (now - user.lastActive > inactiveTimeout) {
          // Remove inactive user
          set(ref(database, `sessions/${sessionId}/activeUsers/${uid}`), null);
        }
      });
    });

    // Clean up on unmount
    return () => {
      clearInterval(heartbeatInterval);
      unsubscribeActiveUsers();
      
      // Only remove presence if we're actually leaving the session
      // (not just if the component is unmounting temporarily)
      if (location.pathname !== `/session/${sessionId}`) {
        set(userPresenceRef, null);
      }
    };
  }, [sessionId, userId, userName, location.pathname, activeUsers]);

  // Listen for game state changes when session ID changes
  useEffect(() => {
    if (!sessionId) {
      setGameState(null);
      return;
    }
    
    const gameStateRef = ref(database, `sessions/${sessionId}/gameState`);
    const unsubscribe = onValue(gameStateRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setGameState(data);
      }
    });
    
    return () => unsubscribe();
  }, [sessionId]);

  // Create a new session
  const createSession = async (asDM: boolean = false): Promise<string> => {
    const newSessionId = uuidv4();
    
    // Initialize game state with the current user's activity data
    const initialGameState = {
      ...defaultGameState,
      userActivities: {
        [userId]: defaultUserActivityData
      }
    };
    
    const sessionData: SessionData = {
      id: newSessionId,
      createdAt: Date.now(),
      lastUpdated: Date.now(),
      activeUsers: {
        [userId]: { 
          name: userName, 
          lastActive: Date.now(),
          role: asDM ? 'DM' : 'Player'
        }
      },
      gameState: initialGameState
    };
    
    await set(ref(database, `sessions/${newSessionId}`), sessionData);
    setSessionId(newSessionId);
    navigate(`/session/${newSessionId}`);
    return newSessionId;
  };

  // Join an existing session
  const joinSession = async (id: string, asDM: boolean = false): Promise<boolean> => {
    const sessionRef = ref(database, `sessions/${id}`);
    const snapshot = await get(sessionRef);
    
    if (!snapshot.exists()) {
      return false;
    }

    const sessionData = snapshot.val();
    const hasDM = Object.values(sessionData.activeUsers || {}).some(
      (user: any) => user.role === 'DM'
    );

    // Don't allow multiple DMs unless the current DM is inactive
    const finalRole = asDM && hasDM ? 'Player' : (asDM ? 'DM' : 'Player');
    
    // Update active users
    await update(ref(database, `sessions/${id}/activeUsers/${userId}`), {
      name: userName,
      lastActive: Date.now(),
      role: finalRole
    });
    
    // Initialize user activity data if not already present
    const userActivityRef = ref(database, `sessions/${id}/gameState/userActivities/${userId}`);
    const activitySnapshot = await get(userActivityRef);
    
    if (!activitySnapshot.exists()) {
      // Initialize with default days based on shared days in turn
      const gameStateRef = ref(database, `sessions/${id}/gameState`);
      const gameStateSnapshot = await get(gameStateRef);
      
      if (gameStateSnapshot.exists()) {
        const gameStateData = gameStateSnapshot.val();
        const sharedDays = gameStateData.sharedDaysInTurn || 3;
        
        // Create default daily activities with the correct number of days
        const dailyActivities: Record<string, string[]> = {};
        for (let i = 1; i <= sharedDays; i++) {
          dailyActivities[`Day ${i}`] = [];
        }
        
        // Set user activity data with correct days
        const userActivityData = {
          ...defaultUserActivityData,
          dailyActivities
        };
        
        await set(userActivityRef, userActivityData);
      } else {
        // Default fallback
        await set(userActivityRef, defaultUserActivityData);
      }
    }
    
    // Update last updated timestamp
    await update(ref(database, `sessions/${id}`), {
      lastUpdated: Date.now()
    });
    
    setSessionId(id);
    navigate(`/session/${id}`);
    return true;
  };

  // Leave the current session
  const leaveSession = () => {
    if (sessionId) {
      // Remove user from active users
      set(ref(database, `sessions/${sessionId}/activeUsers/${userId}`), null);
    }
    
    setSessionId(null);
    setGameState(null);
    navigate('/');
  };

  // Update a specific part of the game state
  const updateGameState = async <K extends keyof SessionData['gameState']>(
    key: K,
    value: SessionData['gameState'][K]
  ): Promise<void> => {
    if (!sessionId) return;
    
    await set(ref(database, `sessions/${sessionId}/gameState/${key}`), value);
    await update(ref(database, `sessions/${sessionId}`), {
      lastUpdated: Date.now()
    });
  };
  
  // Update activity data for a specific user
  const updateUserActivity = async (
    uid: string,
    activityData: UserActivityData
  ): Promise<void> => {
    if (!sessionId) return;
    
    await set(ref(database, `sessions/${sessionId}/gameState/userActivities/${uid}`), activityData);
    await update(ref(database, `sessions/${sessionId}`), {
      lastUpdated: Date.now()
    });
  };
  
  // Update shared days in turn
  const updateSharedDaysInTurn = async (days: number): Promise<void> => {
    if (!sessionId) return;
    
    await set(ref(database, `sessions/${sessionId}/gameState/sharedDaysInTurn`), days);
    await update(ref(database, `sessions/${sessionId}`), {
      lastUpdated: Date.now()
    });
  };

  // Check if the current user is the DM
  const isDM = (): boolean => {
    return activeUsers[userId]?.role === 'DM';
  };

  // Check if the current user can claim the DM role
  const canClaimDM = (): boolean => {
    if (!sessionId) return false;
    
    // Can claim if there's no current DM
    return !Object.entries(activeUsers).some(([id, user]) => 
      id !== userId && user.role === 'DM'
    );
  };

  // Claim the DM role if available
  const claimDMRole = async (): Promise<boolean> => {
    if (!sessionId || !canClaimDM()) return false;
    
    try {
      await update(ref(database, `sessions/${sessionId}/activeUsers/${userId}`), {
        role: 'DM'
      });
      return true;
    } catch (error) {
      console.error('Error claiming DM role:', error);
      return false;
    }
  };

  // Release the DM role
  const releaseDMRole = async (): Promise<void> => {
    if (!sessionId || !isDM()) return;
    
    try {
      await update(ref(database, `sessions/${sessionId}/activeUsers/${userId}`), {
        role: 'Player'
      });
    } catch (error) {
      console.error('Error releasing DM role:', error);
    }
  };

  const contextValue: SessionContextType = {
    sessionId,
    userId,
    userName,
    setUserName,
    gameState,
    createSession,
    joinSession,
    leaveSession,
    updateGameState,
    updateUserActivity,
    updateSharedDaysInTurn,
    activeUsers,
    isDM,
    canClaimDM,
    claimDMRole,
    releaseDMRole
  };

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};